import { getExtensionValue } from "@medplum/core";
import { EpisodeOfCare } from "@medplum/fhirtypes";

import { isValid, parseISO } from "date-fns";

import { Extensions } from "@/fhir/enums/extensions";
import { EpisodeOfCareProps } from "@/types/episode-of-care";
import { joinReferenceString } from "@/utils/medplum";

import { getLocalDateStringInTimezone, usDateFormat } from "./datetime";

export const getEpisodeOfCare = ({
  values,
  subjectId,
  practitionerId,
  episodeOfCareId,
  existingEpisodeOfCare,
}: EpisodeOfCareProps): EpisodeOfCare => {
  const episodeOfCare: EpisodeOfCare = {
    ...(existingEpisodeOfCare && { ...existingEpisodeOfCare }),
    ...(episodeOfCareId && { id: episodeOfCareId }),
    resourceType: "EpisodeOfCare",
    status: values.endDate ? "finished" : values.status,
    patient: { reference: joinReferenceString("Patient", subjectId) },
    ...(!existingEpisodeOfCare?.careManager && {
      careManager: { reference: joinReferenceString("Practitioner", practitionerId) },
    }),
    period: {
      start: resolvePeriodDate(values.startDate, existingEpisodeOfCare?.period?.start),
      ...(values.endDate && {
        end: resolvePeriodDate(values.endDate, existingEpisodeOfCare?.period?.end),
      }),
    },
    type: [
      {
        coding: [values.episodeType],
      },
    ],
    extension: [
      {
        url: Extensions.ArtifactTitle,
        valueString: values.episodeName,
      },
    ],
  };

  return episodeOfCare;
};

export const getEpisodeOfCareDisplay = (episodeOfCare?: EpisodeOfCare) => {
  if (!episodeOfCare) return "";

  const title = getExtensionValue(episodeOfCare, Extensions.ArtifactTitle) as string;
  const startDate = formatEpisodePeriodDate(episodeOfCare.period?.start);

  return startDate ? `${title} - ${startDate}` : title;
};

/**
 * Resolves an Episode of Care `period.start` / `period.end` to the calendar day the user picked.
 *
 * Episode dates are saved as date-only values (`2026-07-01`). Older episodes hold one of two
 * timestamp shapes, read here so they show the same day they did when they were entered:
 * - exactly UTC midnight (`2026-07-01T00:00:00.000Z`, or a `+00:00` offset): a picked or typed
 *   day, read as its UTC date;
 * - any other instant: a day picked before the Mantine v8 upgrade (stored at the user's local
 *   midnight) or the default "today", read as its date in `timeZone`.
 *
 * @param periodDate - FHIR dateTime from `EpisodeOfCare.period`.
 * @param timeZone - IANA zone for instants; defaults to the browser's zone.
 * @returns The calendar day as `yyyy-MM-dd`, or empty string when missing or unparseable.
 */
export function getEpisodePeriodCalendarDate(
  periodDate: string | undefined,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  if (!periodDate) return "";
  if (DATE_ONLY_PATTERN.test(periodDate) || UTC_MIDNIGHT_PATTERN.test(periodDate)) {
    return periodDate.slice(0, 10);
  }
  if (!isValid(parseISO(periodDate))) return "";

  return getLocalDateStringInTimezone(periodDate, timeZone);
}

/**
 * Formats an Episode of Care period date as MM/dd/yyyy on the calendar day the user picked.
 *
 * @param periodDate - FHIR dateTime from `EpisodeOfCare.period`.
 * @returns The formatted date, or empty string when missing or unparseable.
 */
export function formatEpisodePeriodDate(periodDate: string | undefined): string {
  return usDateFormat(getEpisodePeriodCalendarDate(periodDate));
}

/**
 * Picks the value to save for one period boundary. A stored value that already resolves to the
 * submitted day is kept as-is, so saving an episode without touching a date never rewrites it.
 *
 * @param submittedDate - `yyyy-MM-dd` day from the form.
 * @param storedDate - The boundary's current FHIR value, when the episode already exists.
 * @returns The value to write to `EpisodeOfCare.period`.
 */
function resolvePeriodDate(submittedDate: string, storedDate: string | undefined): string {
  if (storedDate && getEpisodePeriodCalendarDate(storedDate) === submittedDate) return storedDate;
  return submittedDate;
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const UTC_MIDNIGHT_PATTERN = /^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?(Z|[+-]00:00)$/;
